import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@/lib/auth';
import { useIsInLti } from '@/shared/lti';
import { useUserPlanManager } from '@/shared/plan';
import { getClientFingerprintId } from '@/shared/utils/fingerprint';
import { buildProFeatureCoachmarkStorage } from './buildProFeatureCoachmarkStorage';
import { BUILD_PRO_FEATURE_COACHMARK_FEATURES, type BuildProFeatureCoachmarkFeature } from './types';
type RegisteredFeatures = Partial<Record<BuildProFeatureCoachmarkFeature, true>>;
export type BuildProFeatureCoachmarkContextValue = {
  activeFeature: BuildProFeatureCoachmarkFeature | null;
  activeStep: number;
  totalSteps: number;
  shouldAnimateInitialOpen: boolean;
  register: (feature: BuildProFeatureCoachmarkFeature, element: HTMLElement | null) => void;
  unregister: (feature: BuildProFeatureCoachmarkFeature) => void;
  consumeInitialOpenAnimation: () => void;
  close: () => void;
  next: () => void;
  previous: () => void;
};
export const BuildProFeatureCoachmarkContext = createContext<BuildProFeatureCoachmarkContextValue | null>(null);
const getScopeKey = ({
  quizUserId,
  fingerprintId
}: {
  quizUserId?: string;
  fingerprintId: string;
}) => {
  if (quizUserId) return `user:${quizUserId}:device:${fingerprintId}`;
  return `guest:device:${fingerprintId}`;
};
export const BuildProFeatureCoachmarkProvider = ({
  children
}: PropsWithChildren) => {
  const {
    user,
    isInitialized
  } = useUser();
  const isInLti = useIsInLti();
  const {
    userPlanManager
  } = useUserPlanManager();
  const [registeredFeatures, setRegisteredFeatures] = useState<RegisteredFeatures>({});
  const [scopeKey, setScopeKey] = useState<string | null>(null);
  const [isDismissed, setIsDismissed] = useState(true);
  const [activeFeature, setActiveFeature] = useState<BuildProFeatureCoachmarkFeature | null>(null);
  const [shouldAnimateInitialOpen, setShouldAnimateInitialOpen] = useState(true);
  const anchorElementsRef = useRef<Partial<Record<BuildProFeatureCoachmarkFeature, HTMLElement>>>({});
  const availableFeatures = useMemo(() => BUILD_PRO_FEATURE_COACHMARK_FEATURES.filter(feature => registeredFeatures[feature] && !(isInLti && feature === 'CONTINUE_NEXT_QUIZSET') && !userPlanManager.canAccessFeature(feature)), [isInLti, registeredFeatures, userPlanManager]);
  useEffect(() => {
    if (!isInitialized || availableFeatures.length === 0) {
      setScopeKey(null);
      setIsDismissed(true);
      return;
    }
    let ignore = false;
    void getClientFingerprintId().then(fingerprintId => {
      if (ignore || !fingerprintId) return;
      const nextScopeKey = getScopeKey({
        quizUserId: user?.quizUserId,
        fingerprintId
      });
      setScopeKey(nextScopeKey);
      setIsDismissed(buildProFeatureCoachmarkStorage.isDismissed(nextScopeKey));
    });
    return () => {
      ignore = true;
    };
  }, [availableFeatures.length, isInitialized, user?.quizUserId]);
  useEffect(() => {
    if (!scopeKey || isDismissed) {
      setActiveFeature(null);
      return;
    }
    if (availableFeatures.length === 0) {
      setActiveFeature(null);
      return;
    }
    setActiveFeature(current => current && availableFeatures.includes(current) ? current : availableFeatures[0]);
  }, [availableFeatures, isDismissed, scopeKey]);
  const dismiss = useCallback(() => {
    if (scopeKey) buildProFeatureCoachmarkStorage.markDismissed(scopeKey);
    setIsDismissed(true);
    setActiveFeature(null);
  }, [scopeKey]);
  const consumeInitialOpenAnimation = useCallback(() => {
    setShouldAnimateInitialOpen(false);
  }, []);
  const register = useCallback((feature: BuildProFeatureCoachmarkFeature, element: HTMLElement | null) => {
    if (element) anchorElementsRef.current[feature] = element;
    setRegisteredFeatures(features => ({
      ...features,
      [feature]: true
    }));
  }, []);
  const unregister = useCallback((feature: BuildProFeatureCoachmarkFeature) => {
    delete anchorElementsRef.current[feature];
    setRegisteredFeatures(({
      [feature]: _removed,
      ...features
    }) => features);
  }, []);
  const next = useCallback(() => {
    const nextFeature = activeFeature ? availableFeatures[availableFeatures.indexOf(activeFeature) + 1] : availableFeatures[0];
    if (nextFeature) {
      setActiveFeature(nextFeature);
      return;
    }
    dismiss();
  }, [activeFeature, availableFeatures, dismiss]);
  const previous = useCallback(() => {
    if (!activeFeature) return;
    const previousFeature = availableFeatures[availableFeatures.indexOf(activeFeature) - 1];
    if (previousFeature) setActiveFeature(previousFeature);
  }, [activeFeature, availableFeatures]);
  const activeIndex = activeFeature ? availableFeatures.indexOf(activeFeature) : -1;
  const activeStep = activeIndex >= 0 ? activeIndex + 1 : 0;
  const value = useMemo<BuildProFeatureCoachmarkContextValue>(() => ({
    activeFeature,
    activeStep,
    totalSteps: availableFeatures.length,
    shouldAnimateInitialOpen,
    register,
    unregister,
    consumeInitialOpenAnimation,
    close: dismiss,
    next,
    previous
  }), [activeFeature, activeStep, availableFeatures.length, consumeInitialOpenAnimation, dismiss, next, previous, register, shouldAnimateInitialOpen, unregister]);
  return <BuildProFeatureCoachmarkContext.Provider value={value} data-sentry-element="unknown" data-sentry-component="BuildProFeatureCoachmarkProvider" data-sentry-source-file="BuildProFeatureCoachmarkProvider.tsx">
      {children}
    </BuildProFeatureCoachmarkContext.Provider>;
};
export const useBuildProFeatureCoachmarkContext = () => {
  const context = useContext(BuildProFeatureCoachmarkContext);
  if (!context) {
    throw new Error('BuildProFeatureCoachmarkProvider not found');
  }
  return context;
};